import type { StepEditorProps } from '@/components/admin/protocoles/contracts';
import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { DirectionField } from '@/components/admin/protocoles/step/direction-field';
import { ExitConditions } from '@/components/admin/protocoles/step/exit-conditions';
import { ObjectiveList } from '@/components/admin/protocoles/step/objective-list';
import { StepOptions } from '@/components/admin/protocoles/step/step-options';
import { StepPanel } from '@/components/admin/protocoles/step/step-panel';
import { StepRulesPanel } from '@/components/admin/protocoles/step/step-rules-panel';
import { VersionedDialog } from '@/components/admin/versioned/versioned-dialog';
import { ActionMenu } from '@/components/ui/action-menu';
import { DuplicateButton } from '@/components/ui/duplicate-button';
import { stepDuplicationBlocked } from '@/lib/duplication';
import { countLabel } from '@/lib/protocol-labels';
import { useFocusOnce } from '@/lib/use-focus-once';
import { PROTOCOL_STEP_NAME_MAX, isEmptyStep, protocolJumps } from '@matter_extractor/shared';
import { Copy, Hand } from 'lucide-react';
import { useRef, useState } from 'react';

/**
 * The selected step, whole: its direction, its objectives, its general rules
 * and its exit conditions (refonte modulaire §7.2, §8.3, §8.4; maquette
 * `renderStep`).
 *
 * CONTROLLED, like the frise beside it: the page holds the draft and the
 * selection, this renders them and asks for a change through `onChangeStep`.
 * It reads no query of its own and writes nothing - `contracts.ts` is the whole
 * of what it is handed, and that is what lets three hands build one editor.
 *
 * ## The layout is the reading order of §7.2
 *
 * Direction, then objectives, then - beside them and STICKY - the rules, then
 * the exit conditions across the full width. The rules are numbers one checks
 * while writing the objectives, so they follow the scroll; the conditions read
 * as sentences about everything above them, so they come last and take the
 * width a sentence needs.
 *
 * ## A farewell step keeps its name and its direction, and nothing else
 *
 * « Elle ne garde que son nom et sa direction : ni objectifs, ni règles
 * générales, ni conditions de sortie, et l'éditeur masque ces trois blocs quand
 * la case est cochée » (§7.2, issue #163). The three are not greyed but GONE,
 * replaced by one dashed line that says where they went and how they come
 * back: a greyed block would invite a reader to wonder what its figures still
 * do, and on a farewell step they do nothing (H76). The direction then takes
 * the width, since no rules column is left to stand beside it.
 *
 * ## Deleting a step asks only when there is something to lose
 *
 * An EMPTY step - no direction, no objective, no condition (`isEmptyStep`) -
 * goes without a dialog: the frise deletes those with a trash can and no
 * confirmation either (§7.1), and asking twice for nothing teaches an operator
 * to click through confirmations. Everything else opens a box that COUNTS what
 * disappears, and - the part that cannot be guessed from the step itself - how
 * many jumps from OTHER steps are about to lose their target, because those
 * become a refusal of « Enregistrer une nouvelle version » (§7.1) on steps the
 * operator is not looking at.
 */

export function StepEditor({
  steps,
  stepId,
  criteria,
  criteriaRead,
  readOnly,
  nameFocusId,
  onNameFocused,
  onChangeStep,
  onMoveStep,
  onDeleteStep,
  onDuplicateStep,
}: StepEditorProps) {
  const [confirming, setConfirming] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  useFocusOnce(nameFocusId === stepId ? nameFocusId : null, nameRef, onNameFocused);

  const index = steps.findIndex((one) => one.id === stepId);
  const step = steps[index];
  if (step === undefined) return null;

  const duplicationBlocked = stepDuplicationBlocked(steps);

  const mandatory = step.objectives.filter((one) => one.mandatory).length;
  const optional = step.objectives.length - mandatory;
  const named = step.name.trim();
  const label = named === '' ? `Étape ${index + 1}` : named;
  const inbound = protocolJumps(steps).filter((jump) => jump.to === step.id).length;

  // The one direction panel, drawn beside the objectives on an ordinary step
  // and alone on a farewell step - hoisted so the two layouts cannot drift.
  const direction = (
    <StepPanel title="direction de l’interviewer" sub={null} seat="l’interviewer" className="">
      <DirectionField
        value={step.direction}
        onChange={(next) => onChangeStep({ ...step, direction: next })}
        readOnly={readOnly}
        placeholder={
          step.farewell
            ? 'Comment l’interviewer prend congé : ce qu’il remercie, ce qu’il retient.'
            : 'Ce que l’interviewer cherche à faire pendant cette étape.'
        }
        ariaLabel="Direction de l’interviewer"
        testId={PROTOCOL_TEST_IDS.stepDirection}
        insertTestId={PROTOCOL_TEST_IDS.insertVariable}
        steps={steps}
        stepId={step.id}
        criteria={criteria}
        minHeightClass="min-h-28"
      />
      <p className="mt-2 text-gray-600 text-xs leading-4">
        Tape <span className="font-semibold">@</span> ou le bouton{' '}
        <span className="font-semibold">{'{ }'}</span> pour insérer la matière d’un objectif ou la
        direction d’un critère. Une variable suit son nom si on la renomme.
      </p>
      {step.farewell ? (
        <p className="mt-3.5 flex items-start gap-2 rounded-md bg-bleu-nuit-tint px-3 py-2.5 text-bleu-nuit-deep text-sm">
          <Hand aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span>
            L’interviewer écrit l’au revoir dans la voix du persona, avec cette direction et la
            conversation. Le composeur disparaît, la personne voit « Continuer ».
          </span>
        </p>
      ) : null}
    </StepPanel>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="rounded-sm bg-gray-100 px-2 font-semibold text-gray-700">{index + 1}</span>
        <input
          ref={nameRef}
          data-testid={PROTOCOL_TEST_IDS.stepName}
          aria-label="Nom de l’étape"
          placeholder="Nom de l’étape"
          value={step.name}
          readOnly={readOnly}
          maxLength={PROTOCOL_STEP_NAME_MAX}
          onChange={(event) => onChangeStep({ ...step, name: event.currentTarget.value })}
          className="min-w-0 flex-1 rounded-md border-2 border-transparent bg-transparent px-1 py-0.5 font-semibold text-bleu-nuit text-h4 read-only:text-gray-800 hover:border-gray-200 focus-visible:border-bleu-nuit focus-visible:outline-none"
        />
        {readOnly ? null : (
          <>
            {/* The step's gutter: the copy icon just before the menu, which
                is here what the cross is elsewhere - the menu is what carries
                « Supprimer l'étape » (§4.2). */}
            <DuplicateButton
              testId={PROTOCOL_TEST_IDS.duplicateStep}
              label="Dupliquer l’étape"
              blocked={duplicationBlocked}
              onClick={onDuplicateStep}
            />
            <ActionMenu
              label="Actions de l’étape"
              data-testid={PROTOCOL_TEST_IDS.stepMenu}
              items={[
                {
                  id: 'move-left',
                  label: 'Déplacer à gauche',
                  disabled: index === 0,
                  onSelect: () => onMoveStep(-1),
                },
                {
                  id: 'move-right',
                  label: 'Déplacer à droite',
                  disabled: index === steps.length - 1,
                  onSelect: () => onMoveStep(1),
                },
                {
                  id: 'duplicate',
                  label: 'Dupliquer',
                  // The SAME glyph as the gutter's: one gesture offered
                  // twice has to read as one (§4.2).
                  icon: <Copy aria-hidden="true" className="size-4 shrink-0" />,
                  /*
                    Closed without a reason of its own, where the gutter icon
                    carries one: a shut menu entry has no room for a sentence,
                    and its twin sits an inch away saying it on hover.
                  */
                  disabled: duplicationBlocked !== null,
                  onSelect: onDuplicateStep,
                },
                {
                  id: 'delete',
                  label: 'Supprimer l’étape',
                  onSelect: () => {
                    if (isEmptyStep(step)) {
                      onDeleteStep();
                      return;
                    }
                    setConfirming(true);
                  },
                },
              ]}
            />
          </>
        )}
      </div>

      <StepOptions step={step} readOnly={readOnly} onChangeStep={onChangeStep} />

      {step.farewell ? (
        <>
          {direction}
          <p
            data-testid={PROTOCOL_TEST_IDS.stepFarewellHidden}
            className="rounded-md border-2 border-gray-200 border-dashed px-4 py-3 text-caption text-gray-500"
          >
            Objectifs, règles générales et conditions de sortie ne s’appliquent pas à une étape
            d’adieu. Décocher la case les rend vides, avec les règles par défaut.
          </p>
        </>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div className="min-w-0 space-y-6">
              {direction}

              <StepPanel
                title="objectifs"
                sub={`${countLabel(mandatory, 'obligatoire')} · ${countLabel(optional, 'optionnel')}`}
                seat={null}
                className=""
              >
                <ObjectiveList
                  step={step}
                  steps={steps}
                  criteria={criteria}
                  criteriaRead={criteriaRead}
                  readOnly={readOnly}
                  onChangeObjectives={(objectives) =>
                    onChangeStep({ ...step, objectives: [...objectives] })
                  }
                />
              </StepPanel>
            </div>

            <div className="min-w-0">
              <StepPanel
                title="règles générales"
                sub={null}
                seat={null}
                className="lg:sticky lg:top-[calc(var(--spacing-admin-bar)+1rem)]"
              >
                <StepRulesPanel
                  step={step}
                  steps={steps}
                  readOnly={readOnly}
                  onChangeRules={(rules) => onChangeStep({ ...step, rules })}
                />
              </StepPanel>
            </div>
          </div>

          <StepPanel
            title="conditions de sortie"
            sub="évaluées dans l’ordre après les garde-fous · la première vraie gagne"
            seat={null}
            className=""
          >
            <ExitConditions
              step={step}
              steps={steps}
              criteria={criteria}
              readOnly={readOnly}
              onChangeConditions={(conditions) =>
                onChangeStep({ ...step, conditions: [...conditions] })
              }
            />
          </StepPanel>
        </>
      )}

      {confirming ? (
        <VersionedDialog
          data-testid={PROTOCOL_TEST_IDS.deleteStepConfirm}
          title={`Supprimer l’étape « ${label} » ?`}
          description={
            <>
              <span className="block">
                {countLabel(step.objectives.length, 'objectif')} et{' '}
                {countLabel(step.conditions.length, 'condition de sortie', 'conditions de sortie')}{' '}
                disparaissent avec elle.
              </span>
              {inbound === 0 ? null : (
                <span className="mt-1.5 block">
                  {countLabel(inbound, 'saut')} d’autres étapes visent celle-ci : ils perdront leur
                  cible et l’enregistrement d’une version sera refusé jusqu’à correction.
                </span>
              )}
            </>
          }
          confirmLabel="Supprimer l’étape"
          confirmTone="destructive"
          busy={false}
          error={null}
          onConfirm={() => {
            setConfirming(false);
            onDeleteStep();
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
